import { useCallback, useEffect, useRef, useState } from "react";

import { useDebouncedValue } from "@/shared/hooks/useDebouncedValue";
import { readApiStatus } from "@/shared/utils/apiError";

import { CARDHOLDER_BOARD_PAGE_SIZE } from "../constants/cardholderOnboarding.constants";
import { CardholderOnboardingService } from "../services/cardholderOnboarding.service";
import type { CardholderBoardResponse, CardholderBucket } from "../types/cardholderOnboarding.types";

export type CardholderBoardError = "denied" | "failed";

export interface UseCardholderBoardResult {
  board: CardholderBoardResponse | null;
  loading: boolean;
  error: CardholderBoardError | null;
  bucket: CardholderBucket | null;
  setBucket: (bucket: CardholderBucket | null) => void;
  search: string;
  setSearch: (search: string) => void;
  page: number;
  setPage: (page: number) => void;
  reload: () => Promise<void>;
}

/** One organization's board: filter, search and page, with answers for an earlier query dropped. */
export function useCardholderBoard(organizationId: string | null): UseCardholderBoardResult {
  const [bucket, setBucket] = useState<CardholderBucket | null>(null);
  const [search, setSearch] = useState("");
  const term = useDebouncedValue(search.trim(), 350);
  // The page belongs to one query; changing the organization, the filter or the search starts at 1.
  const queryKey = `${organizationId}|${bucket}|${term}`;
  const [pageState, setPageState] = useState({ key: queryKey, page: 1 });
  const page = pageState.key === queryKey ? pageState.page : 1;
  const [data, setData] = useState<CardholderBoardResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<CardholderBoardError | null>(null);
  const requestRef = useRef(0);

  const load = useCallback(async () => {
    const id = ++requestRef.current;
    if (!organizationId) {
      setData(null);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await CardholderOnboardingService.board({
        organization_id: organizationId,
        bucket: bucket ?? undefined,
        search: term || undefined,
        skip: (page - 1) * CARDHOLDER_BOARD_PAGE_SIZE,
        limit: CARDHOLDER_BOARD_PAGE_SIZE,
      });
      if (id !== requestRef.current) return;
      setData(response);
    } catch (err) {
      if (id !== requestRef.current) return;
      const status = readApiStatus(err);
      setError(status === 401 || status === 403 ? "denied" : "failed");
    } finally {
      if (id === requestRef.current) setLoading(false);
    }
  }, [organizationId, bucket, term, page]);

  useEffect(() => {
    void load();
  }, [load]);

  const setPage = useCallback((next: number) => setPageState({ key: queryKey, page: next }), [queryKey]);

  return {
    // A previous organization's answer never shows under the one just picked.
    board: data?.organization.id === organizationId ? data : null,
    loading,
    error,
    bucket,
    setBucket,
    search,
    setSearch,
    page,
    setPage,
    reload: load,
  };
}
